import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Modal,
  Switch,
  TextInput
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type {
  Fee,
  FeeType
} from "@dealer-center/ddc-pages-fee-manager-interface";
import {
  parseCurrency,
  validateFeeAmount,
  validateFeeName
} from "../utils/format";

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;

  /**
   * MUI Override: Style Interstate TextInput labels to match design
   * Reason: Interstate TextInput does not expose variant or style props for labels.
   * Design requires uppercase, bold, small font (12px) for all form labels.
   * Without override, labels would be default MUI styling (14px, normal weight, sentence case).
   */
  .MuiFormLabel-root.MuiInputLabel-root {
    font-size: 12px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    color: #374151 !important;
  }
`;

const AmountRow = styled(FormRow)`
  /* Constrain width to accommodate 8 digits plus currency symbol */
  max-width: 168px;

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const IncludeRow = styled.div<{ $locale: string }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  /**
   * MUI Override: Hide checkmark icon inside Switch thumb
   * Reason: Interstate Switch renders a checkmark SVG icon inside the thumb by default.
   * No prop exists to disable this icon. Design requires a plain thumb without icon.
   */
  .MuiSwitch-thumb svg {
    display: none;
  }

  /**
   * MUI Override: Style Interstate Switch label to match TextInput labels
   * Reason: Interstate Switch does not expose props to customize label styling.
   * Updated to use standard case (not uppercase) for better readability.
   */
  .MuiFormControlLabel-label,
  label.MuiFormControlLabel-label,
  span.MuiFormControlLabel-label,
  .MuiInputLabel-root {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: #374151 !important;
    opacity: 1 !important;
    margin-top: 4px !important;
  }

  /* 
   * MUI Override: Align Switch label with Amount field label
   * Keep horizontal layout but adjust positioning to match text input labels
   * Locale-aware: fr-CA label is longer, needs additional 27px offset
   */
  .ids-toggle-switch-container.ids-input-layout-horizontal {
    align-items: center !important;

    /* Position label to align with text input labels - shift left based on locale */
    .MuiInputLabel-root.MuiInputLabel-shrink {
      margin-left: ${props =>
        props.$locale === "fr-CA" ? "-13px" : "-40px"} !important;
      margin-right: 8px !important;
    }

    /* Reduce spacing between label and switch by targeting the wrapper div */
    > .MuiBox-root {
      margin-left: 0 !important;
      padding-left: 0 !important;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 48px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-top: 32px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    margin-top: 24px;
    flex-direction: column;
    gap: 8px;
  }
`;

const StyledModal = styled(Modal)`
  /* Add padding to modal title */
  .ids-modal-title {
    padding: 24px 24px 0 24px !important;
  }

  @media (max-width: 768px) {
    .ids-modal-title {
      padding: 16px 16px 0 16px !important;
    }

    .ids-modal-content {
      padding: 16px !important;
    }
  }

  @media (max-width: 480px) {
    .ids-modal-title {
      padding: 12px 12px 0 12px !important;
    }

    .ids-modal-content {
      padding: 12px !important;
    }
  }
`;

export interface AddFeeModalProps {
  isOpen: boolean;
  type: FeeType;
  existingFees: Fee[];
  onAdd: (data: {
    name: string;
    amount: number | null;
    include: boolean;
  }) => void;
  onCancel: () => void;
}

export const AddFeeModal = ({
  isOpen,
  type,
  existingFees,
  onAdd,
  onCancel
}: AddFeeModalProps) => {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [include, setInclude] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; amount?: string }>({});
  const nameInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setName("");
      setAmount("");
      setInclude(false);
      setErrors({});

      // Focus and select the name field
      setTimeout(() => {
        const input = nameInputRef.current?.querySelector("input");
        if (input) {
          input.focus();
          input.select();
        }
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const newErrors: { name?: string; amount?: string } = {};

    if (!validateFeeName(name)) {
      newErrors.name = String(Locale.getText("FEEMANAGER_FEENAME_ERROR"));
    } else {
      // Only check for duplicate if name is valid (not empty)
      const trimmedName = name.trim();
      const isDuplicate = existingFees.some(
        fee => fee.name.toLowerCase() === trimmedName.toLowerCase()
      );

      if (isDuplicate) {
        newErrors.name = String(
          Locale.getText("FEEMANAGER_DUPLICATE_NAME_ERROR")
        );
      }
    }

    const parsedAmount = parseCurrency(amount);
    if (amount && !validateFeeAmount(parsedAmount)) {
      newErrors.amount = String(Locale.getText("FEEMANAGER_AMOUNT_ERROR"));
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onAdd({
      name: name.trim(),
      amount: parsedAmount,
      include
    });
  };

  const vehicleType = type === "new" ? "New" : "Pre-Owned";
  const modalTitle = Locale.getText(
    type === "new"
      ? "FEEMANAGER_ADDFEE_MODAL_TITLE_NEW"
      : "FEEMANAGER_ADDFEE_MODAL_TITLE_PREOWNED"
  );

  // For fr-CA locale, append $ instead of prepending it
  const isFrenchCanadian = Locale.getLocale() === "fr-CA";
  const currencyProps = isFrenchCanadian
    ? { inputSuffix: "$" }
    : { inputPrefix: "$" };

  // Prevent closing on backdrop click but allow X button and ESC key
  const handleModalHide = ((...args: unknown[]) => {
    const reason = args[1] as string | undefined;
    if (reason === "backdropClick") {
      return;
    }
    onCancel();
  }) as () => void;

  return (
    <StyledModal
      header={modalTitle}
      show={isOpen}
      size="large"
      onHide={handleModalHide}
    >
      <FormRow ref={nameInputRef}>
        <TextInput
          id="fee-name"
          label={String(Locale.getText("FEEMANAGER_NAME"))}
          name="fee-name"
          placeholder={String(Locale.getText("FEEMANAGER_FEENAME_PLACEHOLDER"))}
          value={name}
          onChange={e => {
            setName(e.target.value);
            if (errors.name) {
              setErrors(prev => ({ ...prev, name: undefined }));
            }
          }}
        />
        {errors.name && (
          <span style={{ color: "red", fontSize: "12px" }}>{errors.name}</span>
        )}
      </FormRow>

      <AmountRow>
        <TextInput
          id="fee-amount"
          label={String(Locale.getText("FEEMANAGER_AMOUNT"))}
          name="fee-amount"
          placeholder={String(Locale.getText("FEEMANAGER_AMOUNT_PLACEHOLDER"))}
          value={amount}
          {...currencyProps}
          onChange={e => {
            const value = e.target.value;
            // Normalize comma to period for internal storage (fr-CA support)
            const normalized = value.replace(/,/g, ".");
            // Allow only numbers and one decimal point
            let filtered = normalized.replace(/[^0-9.]/g, "");

            // Ensure only one decimal point
            const parts = filtered.split(".");
            if (parts.length > 2) {
              filtered = parts[0] + "." + parts.slice(1).join("");
            }

            setAmount(filtered);
            if (errors.amount) {
              setErrors(prev => ({ ...prev, amount: undefined }));
            }
          }}
          onKeyDown={e => {
            // Allow numeric keys, decimal point, and control keys
            const allowedKeys = [
              "Backspace",
              "Delete",
              "Tab",
              "ArrowLeft",
              "ArrowRight",
              "ArrowUp",
              "ArrowDown"
            ];
            const isNumeric = /^[0-9]$/.test(e.key);
            const isDecimal = e.key === "." || e.key === ",";

            if (!allowedKeys.includes(e.key) && !isNumeric && !isDecimal) {
              e.preventDefault();
              return;
            }

            // Prevent multiple decimal points (commas are normalized to periods in state)
            if (isDecimal && amount.includes(".")) {
              e.preventDefault();
              return;
            }

            // Prevent entering more than 2 decimal places
            if (isNumeric && amount.includes(".")) {
              const parts = amount.split(".");
              if (parts[1] && parts[1].length >= 2) {
                e.preventDefault();
                return;
              }
            }
          }}
        />
        {errors.amount && (
          <span style={{ color: "red", fontSize: "12px" }}>
            {errors.amount}
          </span>
        )}
      </AmountRow>

      <IncludeRow $locale={Locale.getLocale()}>
        <Switch
          checked={include}
          id="fee-include"
          label={String(Locale.getText("FEEMANAGER_INCLUDEINTOTAL"))}
          value={include ? "checked" : "unchecked"}
          onClick={() => setInclude(!include)}
        />
      </IncludeRow>

      <ButtonGroup>
        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="secondary"
          onClick={onCancel}
        >
          {String(Locale.getText("FEEMANAGER_CANCEL"))}
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="primary"
          onClick={handleSubmit}
        >
          {String(Locale.getText("FEEMANAGER_ADDFEE"))}
        </Button>
      </ButtonGroup>
    </StyledModal>
  );
};
